import { BlogPost } from '../models/Post';
import { useState } from 'react';
import ViewBlogPostModal from './modals/ViewBlogPostModal';
import { Download } from 'lucide-react';
import DownloadSignUpModal from './modals/DownloadSignUpModal';
import { PAGE_TITLES } from '../constants/pageTracking';
import { trackPageView } from '../utils/trackingUtils';

interface BlogPostCardProps extends BlogPost {
    className?: string;
}

export function BlogPostCard(props: BlogPostCardProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    // Track blog post interactions
    const trackPostInteraction = (action: string) => {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'blog_interaction', {
                page_title: PAGE_TITLES.BLOG_POST.tracking,
                post_title: props.Title,
                post_id: props.Id,
                author: props.Author.Name,
                action: action,
                tags: props.Tags.join(',')
            });
        }
    };

    const handlePostClick = () => {
        trackPageView('BLOG_POST', 'blog_post');
        setIsModalOpen(true);
    };

    const handleDownloadClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        trackPostInteraction('download_attempt');
        setIsDownloadModalOpen(true);
    };

    return (
        <>
            <div 
                onClick={handlePostClick}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
            >
                <div className="p-6">
                    <header className="mb-4">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            <button 
                                onClick={handlePostClick}
                                className="hover:text-primary text-left"
                            >
                                {props.Title}
                            </button>
                        </h2>
                        <div className="flex items-center text-sm text-gray-600 gap-4">
                            <span>{new Date(props.CreatedAt).toLocaleDateString()}</span>
                            <span>By {props.Author.Name}</span>
                        </div>
                    </header>
                    
                    {props.ImageUrl && (
                        <img 
                            src={props.ImageUrl} 
                            alt={props.Title}
                            className="w-full h-48 object-cover mb-4 rounded"
                            loading="lazy"
                            onLoad={() => trackPostInteraction('image_loaded')}
                        />
                    )}
                    
                    <p className="text-gray-600 mb-4">
                        {props.Excerpt}
                    </p>
                    
                    <footer className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex flex-wrap gap-2">
                            {props.Tags.map((tag, index) => (
                                <span 
                                    key={index}
                                    className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-600"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        trackPostInteraction(`tag_click_${tag}`);
                                    }}
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            {props.DownloadableFile && (
                                <button 
                                    onClick={handleDownloadClick}
                                    className="flex items-center gap-1 text-gray-600 hover:text-primary border border-gray-300 px-3 py-1.5 rounded-md text-sm"
                                >
                                    <Download size={16} />
                                    <span>Download</span>
                                </button>
                            )}
                            <button 
                                onClick={handlePostClick}
                                className="text-primary hover:text-primary/80 font-medium whitespace-nowrap"
                            >
                                Read more →
                            </button>
                        </div>
                    </footer>
                </div>
            </div>

            <ViewBlogPostModal 
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    trackPostInteraction('close_modal');
                }}
                post={props}
            />

            {props.DownloadableFile && (
                <DownloadSignUpModal
                    isOpen={isDownloadModalOpen}
                    onClose={() => {
                        setIsDownloadModalOpen(false);
                        trackPostInteraction('close_download_modal');
                    }}
                    downloadFileName={props.DownloadableFile}
                />
            )}
        </>
    );
}